/**
 * Financial Insights Dashboard
 *
 * Main dashboard page for displaying financial analytics from QuickBooks Online.
 * Features:
 * - KPI display grid
 * - Date range filtering
 * - QuickBooks connection status check
 * - Loading and error states
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import {
  useGetDashboardKPIsQuery,
  useGetRevenueOverTimeQuery,
  useGetARAgingBucketsQuery,
  useGetAPAgingBucketsQuery,
  useGetCustomerLeaderboardQuery,
  useGetRevenueByAccountQuery,
  useGetExpenseTrendsQuery,
  useGetProfitTrendQuery,
  useGetNewVsReturningRevenueQuery,
  useGetCashFlowQuery,
} from '../../api/financialInsightsApi';
import { useGetConnectionStatusQuery } from '../../api/quickbooksApi';
import { DateRangeSelector } from '../../components/financialInsights/DateRangeSelector';
import { KPIGrid } from '../../components/financialInsights/KPIGrid';
import { EmptyState } from '../../components/financialInsights/EmptyState';
import ConnectionStatus from '../quickbooks/components/ConnectionStatus';
import { RevenueOverTimeChart } from '../../components/financialInsights/charts/RevenueOverTimeChart';
import { ARAgingChart } from '../../components/financialInsights/charts/ARAgingChart';
import { APAgingChart } from '../../components/financialInsights/charts/APAgingChart';
import { CustomerLeaderboardChart } from '../../components/financialInsights/charts/CustomerLeaderboardChart';
import { RevenueBreakdownChart } from '../../components/financialInsights/charts/RevenueBreakdownChart';
import { ExpenseTrendsChart } from '../../components/financialInsights/charts/ExpenseTrendsChart';
import { ProfitTrendChart } from '../../components/financialInsights/charts/ProfitTrendChart';
import { NewVsReturningChart } from '../../components/financialInsights/charts/NewVsReturningChart';
import { CashFlowChart } from '../../components/financialInsights/charts/CashFlowChart';
import FeatureGate from '../../components/common/FeatureGate';
import Header from '../../components/layout/Header';
import { useOwnerManagerAccess } from '../../hooks/useToolAccess';
import type { DateRangePreset } from '../../types/financialInsights';
import NoToolAccess from '../../components/common/NoToolAccess';
import { getDateRangeForPreset } from '../../utils/dateRangeUtils';

/**
 * Financial Insights Dashboard component
 */
export const FinancialInsightsDashboard: React.FC = () => {
  const { studioId: urlStudioId } = useParams<{ studioId: string }>();

  // Get user's eligible studios (Owner/Manager only)
  const {
    eligibleStudios,
    hasToolAccess,
    isLoading: isStudiosLoading,
  } = useOwnerManagerAccess();

  // Use studio ID from URL, or first eligible studio if accessing via /financial-insights
  const studioId = urlStudioId || eligibleStudios[0]?.id;

  // Date range state
  const [dateRangePreset, setDateRangePreset] =
    useState<DateRangePreset>('this_month');
  const [customStartDate, setCustomStartDate] = useState<string | undefined>();
  const [customEndDate, setCustomEndDate] = useState<string | undefined>();

  // Loading state with minimum display time
  const [showLoading, setShowLoading] = useState(false);
  const loadingTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Chart granularity state
  const [revenueGranularity, setRevenueGranularity] = useState<
    'daily' | 'weekly' | 'monthly'
  >('monthly');

  // Check QuickBooks connection status
  const {
    data: connectionStatus,
    isLoading: isConnectionLoading,
    error: connectionError,
  } = useGetConnectionStatusQuery(studioId || '', {
    skip: !studioId,
  });

  // Fetch dashboard KPIs
  const {
    data: kpis,
    isLoading: isKPIsLoading,
    isFetching: isKPIsFetching,
    error: kpisError,
    refetch,
  } = useGetDashboardKPIsQuery(
    {
      studioId: studioId || '',
      preset: dateRangePreset,
      start_date: customStartDate,
      end_date: customEndDate,
    },
    {
      skip: !studioId || !connectionStatus?.is_connected,
      refetchOnMountOrArgChange: true,
    }
  );

  // Fetch chart data
  const { data: revenueOverTime, isLoading: revenueLoading } =
    useGetRevenueOverTimeQuery(
      {
        studioId: studioId || '',
        preset: dateRangePreset,
        start_date: customStartDate,
        end_date: customEndDate,
        granularity: revenueGranularity,
      },
      {
        skip: !studioId || !connectionStatus?.is_connected,
      }
    );

  const { data: arAging, isLoading: arLoading } = useGetARAgingBucketsQuery(
    { studioId: studioId || '' },
    {
      skip: !studioId || !connectionStatus?.is_connected,
    }
  );

  const { data: apAging, isLoading: apLoading } = useGetAPAgingBucketsQuery(
    { studioId: studioId || '' },
    {
      skip: !studioId || !connectionStatus?.is_connected,
    }
  );

  const { data: customerLeaderboard, isLoading: leaderboardLoading } =
    useGetCustomerLeaderboardQuery(
      {
        studioId: studioId || '',
        preset: dateRangePreset,
        start_date: customStartDate,
        end_date: customEndDate,
        limit: 10,
      },
      {
        skip: !studioId || !connectionStatus?.is_connected,
      }
    );

  const { data: revenueByAccount, isLoading: accountLoading } =
    useGetRevenueByAccountQuery(
      {
        studioId: studioId || '',
        preset: dateRangePreset,
        start_date: customStartDate,
        end_date: customEndDate,
      },
      {
        skip: !studioId || !connectionStatus?.is_connected,
      }
    );

  const { data: expenseTrends, isLoading: expenseLoading } =
    useGetExpenseTrendsQuery(
      {
        studioId: studioId || '',
        preset: dateRangePreset,
        start_date: customStartDate,
        end_date: customEndDate,
      },
      {
        skip: !studioId || !connectionStatus?.is_connected,
      }
    );

  const { data: profitTrend, isLoading: profitLoading } =
    useGetProfitTrendQuery(
      {
        studioId: studioId || '',
        preset: dateRangePreset,
        start_date: customStartDate,
        end_date: customEndDate,
        granularity: 'monthly',
      },
      {
        skip: !studioId || !connectionStatus?.is_connected,
      }
    );

  const { data: newVsReturning, isLoading: newVsReturningLoading } =
    useGetNewVsReturningRevenueQuery(
      {
        studioId: studioId || '',
        preset: dateRangePreset,
        start_date: customStartDate,
        end_date: customEndDate,
      },
      {
        skip: !studioId || !connectionStatus?.is_connected,
      }
    );

  const { data: cashFlow, isLoading: cashFlowLoading } = useGetCashFlowQuery(
    {
      studioId: studioId || '',
      preset: dateRangePreset,
      start_date: customStartDate,
      end_date: customEndDate,
      granularity: 'monthly',
    },
    {
      skip: !studioId || !connectionStatus?.is_connected,
    }
  );

  /**
   * Handle date range change
   */
  const handleDateRangeChange = (
    preset: DateRangePreset,
    startDate?: string,
    endDate?: string
  ) => {
    setDateRangePreset(preset);

    // If switching to custom mode without dates, pre-populate with current preset's date range
    if (preset === 'custom' && !startDate && !endDate) {
      const currentDates = getDateRangeForPreset(dateRangePreset);
      if (currentDates) {
        setCustomStartDate(currentDates.startDate);
        setCustomEndDate(currentDates.endDate);
        // Immediately emit the change with the calculated dates
        // Note: We don't call onChange here to avoid recursion
        // The DateRangeSelector will handle the actual onChange call
      }
    } else {
      setCustomStartDate(startDate);
      setCustomEndDate(endDate);
    }
  };

  /**
   * Handle manual refresh
   */
  const handleRefresh = () => {
    refetch();
  };

  /**
   * Determine if any data is currently loading or fetching
   * Note: isFetching remains true while background refetch happens, even if data exists
   */
  const isAnyDataLoading =
    isKPIsLoading ||
    isKPIsFetching ||
    revenueLoading ||
    arLoading ||
    apLoading ||
    leaderboardLoading ||
    accountLoading ||
    expenseLoading ||
    profitLoading ||
    newVsReturningLoading ||
    cashFlowLoading;

  // Manage loading overlay with minimum display time (800ms)
  React.useEffect(() => {
    if (isAnyDataLoading) {
      // Show loading immediately
      setShowLoading(true);

      // Clear any existing timer
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    } else if (showLoading) {
      // Keep loading visible for at least 800ms after data loads
      loadingTimerRef.current = setTimeout(() => {
        setShowLoading(false);
        loadingTimerRef.current = null;
      }, 800);
    }

    // Cleanup timer on unmount
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [isAnyDataLoading, showLoading]);

  // Show no access message if user doesn't have required permissions
  if (!isStudiosLoading && !hasToolAccess) {
    return (
      <NoToolAccess
        toolName="Financial Insights"
        requiredRoles={['owner', 'manager']}
      />
    );
  }

  // Loading state - wait for studios to load before checking connection
  if (isStudiosLoading || isConnectionLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Connection error state
  if (connectionError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to check QuickBooks connection status. Please try again later.
        </Alert>
      </Box>
    );
  }

  // QuickBooks not connected
  if (!connectionStatus?.is_connected) {
    return (
      <>
        <Header />
        <FeatureGate
          requirement={{ minimumPlan: 'growth' }}
          featureName="Financial Insights Dashboard"
          upgradeDescription="Access comprehensive financial analytics powered by QuickBooks Online. View revenue trends, cash flow, accounts receivable/payable, and customer insights to make data-driven business decisions."
        >
          <EmptyState studioId={studioId || ''} />
        </FeatureGate>
      </>
    );
  }

  return (
    <>
      <Header />
      <FeatureGate
        requirement={{ minimumPlan: 'growth' }}
        featureName="Financial Insights Dashboard"
        upgradeDescription="Access comprehensive financial analytics powered by QuickBooks Online. View revenue trends, cash flow, accounts receivable/payable, and customer insights to make data-driven business decisions."
      >
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Financial Insights
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={isKPIsLoading}
              sx={{ ml: 2 }}
            >
              Refresh
            </Button>
          </Box>

          {/* QuickBooks Connection Status */}
          <Box sx={{ mb: 3 }}>
            <ConnectionStatus studioId={studioId || ''} />
          </Box>

          {/* Date Range Selector */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <DateRangeSelector
              selectedPreset={dateRangePreset}
              startDate={customStartDate}
              endDate={customEndDate}
              onChange={handleDateRangeChange}
            />
          </Paper>

          {/* Error State */}
          {kpisError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Failed to load financial insights. Please try again or check your
              QuickBooks connection.
            </Alert>
          )}

          {/* KPI Grid */}
          <KPIGrid kpis={kpis || null} loading={showLoading} />

          {/* Charts Section */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Financial Analytics
            </Typography>

            {/* Revenue Over Time - Full Width */}
            <Box sx={{ mb: 3 }}>
              <RevenueOverTimeChart
                data={revenueOverTime}
                loading={revenueLoading}
                granularity={revenueGranularity}
                onGranularityChange={setRevenueGranularity}
              />
            </Box>

            {/* AR/AP Aging - Side by Side */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 3,
                mb: 3,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <ARAgingChart data={arAging} loading={arLoading} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <APAgingChart data={apAging} loading={apLoading} />
              </Box>
            </Box>

            {/* Customer Leaderboard - Full Width */}
            <Box sx={{ mb: 3 }}>
              <CustomerLeaderboardChart
                data={customerLeaderboard}
                loading={leaderboardLoading}
              />
            </Box>

            {/* Revenue Breakdown - Single Chart for MVP */}
            <Box sx={{ width: { xs: '100%', md: '50%' }, mb: 3 }}>
              <RevenueBreakdownChart
                data={revenueByAccount}
                loading={accountLoading}
                title="Revenue by Income Account"
              />
            </Box>

            {/* Expense Trends - Full Width */}
            <Box sx={{ mb: 3 }}>
              <ExpenseTrendsChart
                data={expenseTrends}
                loading={expenseLoading}
              />
            </Box>

            {/* Profit & Cash Flow - Side by Side */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 3,
                mb: 3,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <ProfitTrendChart data={profitTrend} loading={profitLoading} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <CashFlowChart data={cashFlow} loading={cashFlowLoading} />
              </Box>
            </Box>

            {/* New vs Returning Revenue */}
            <Box sx={{ width: { xs: '100%', md: '50%' } }}>
              <NewVsReturningChart
                data={newVsReturning}
                loading={newVsReturningLoading}
              />
            </Box>
          </Box>
        </Box>
      </FeatureGate>
    </>
  );
};

export default FinancialInsightsDashboard;
